import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  attachInitiativeIntegrator,
  detachInitiativeIntegrator,
  getInitiativeIntegratorCandidates,
  getInitiativeIntegrators,
} from '../initiative.service';
import { resultMessage } from '../../../../../service/apiResult';
import InlineAlert from '../../../../components/inlineAlert';
import NotificationSnackbar from '../../../../components/notificationSnackbar';
import { detailPageCardSx as pageCardSx, detailSectionTitleSx } from '../../../../styles/detailStyles';
import {
  confirmationDialogSx,
  confirmationIconSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
  confirmationTextSx,
  confirmationTitleSx,
} from '../../../../styles/dialogStyles';

const headCellSx = { fontWeight: 600, color: '#1C1B1F', fontSize: 14, whiteSpace: 'nowrap' };

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

const candidateLabel = (option) =>
  [option.name, option.ecosystemName, option.countryName].filter(Boolean).join(' · ');

/**
 * Decision D5: eKutir attaches Initiative Based Ecosystem Integrators to this Initiative. Attached
 * integrators show in the EI console's Initiative view and let DAREs of their country join it.
 * A-07: integrators that picked this Initiative at eK Link sign-up are listed too, and detaching one
 * also removes the Initiative from its sign-up choice.
 */
export default function InitiativeIntegrators({ initiativeId }) {
  const [attached, setAttached] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toDetach, setToDetach] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!initiativeId) return;
    setLoading(true);
    const [list, options] = await Promise.all([
      getInitiativeIntegrators(initiativeId),
      getInitiativeIntegratorCandidates(initiativeId),
    ]);
    if (list.success) setAttached(list.data || []);
    if (options.success) setCandidates(options.data || []);
    if (!list.success || !options.success) {
      setError(resultMessage(list.success ? options : list, 'Could not load the Ecosystem Integrators.'));
    }
    setLoading(false);
  }, [initiativeId]);

  useEffect(() => {
    load();
  }, [load]);

  const attach = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    const res = await attachInitiativeIntegrator(initiativeId, selected.userId);
    setSaving(false);
    if (res.success) {
      setSuccess(`${selected.name} attached to the Initiative`);
      setSelected(null);
      load();
    } else {
      setError(resultMessage(res));
    }
  };

  const closeDetach = () => {
    setToDetach(null);
    setReason('');
  };

  const detach = async () => {
    const integrator = toDetach;
    setToDetach(null);
    setSaving(true);
    setError('');
    const res = await detachInitiativeIntegrator(initiativeId, integrator.userId, reason.trim());
    setReason('');
    setSaving(false);
    if (res.success) {
      setSuccess(`${integrator.name} detached from the Initiative`);
      load();
    } else {
      setError(resultMessage(res));
    }
  };

  return (
    <Box sx={{ ...pageCardSx, mt: 3 }}>
      <Typography sx={{ ...detailSectionTitleSx, mb: 0.5 }}>Ecosystem Integrators</Typography>
      <Typography sx={{ fontSize: 14, lineHeight: '20px', color: '#49454F', mb: 2.5 }}>
        Initiative Based Ecosystem Integrators that deliver this Initiative, attached here or chosen at eK Link
        sign-up. Area integrators that joined it approve which of these work in their area, and their DAREs
        see only those.
      </Typography>

      <InlineAlert message={error} onClose={() => setError('')} sx={{ mb: 2 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
        <Autocomplete
          sx={{ flex: 1, maxWidth: { sm: 560 } }}
          options={candidates}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          getOptionLabel={candidateLabel}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          loading={loading}
          noOptionsText="No other approved Initiative Based Ecosystem Integrators"
          renderInput={(params) => (
            <TextField {...params} size="small" label="Initiative Based Ecosystem Integrator" />
          )}
        />
        <Button
          variant="contained"
          disabled={!selected || saving}
          onClick={attach}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:link-variant" />}
          sx={{ ...confirmationPrimaryButtonSx, height: 40 }}
        >
          Attach
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Integrator</TableCell>
              <TableCell sx={headCellSx}>Ecosystem</TableCell>
              <TableCell sx={headCellSx}>Country</TableCell>
              <TableCell sx={headCellSx}>Access</TableCell>
              <TableCell sx={headCellSx}>Attached on</TableCell>
              <TableCell sx={headCellSx} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attached.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: '#49454F', py: 3, textAlign: 'center' }}>
                  {loading ? 'Loading…' : 'No Ecosystem Integrator is attached yet.'}
                </TableCell>
              </TableRow>
            )}
            {attached.map((row) => (
              <TableRow key={row.userId}>
                <TableCell>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#49454F' }}>
                    {[row.email, row.phoneNumber].filter(Boolean).join(' · ')}
                  </Typography>
                </TableCell>
                <TableCell>{row.ecosystemName || '-'}</TableCell>
                <TableCell>{row.countryName || '-'}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.open ? 'Open' : 'Captive'} />
                </TableCell>
                <TableCell>
                  {row.via === 'RUNS' ? 'Runs this Initiative' : formatDate(row.attachedAt)}
                  {row.signupChoice && row.via !== 'RUNS' && (
                    <Chip size="small" variant="outlined" label="Chosen at sign-up" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.via === 'RUNS' ? (
                    '-'
                  ) : (
                    <Button
                      size="small"
                      disabled={saving}
                      onClick={() => setToDetach(row)}
                      sx={{ textTransform: 'none', color: '#DA342E' }}
                      startIcon={<Icon icon="mdi:link-variant-off" />}
                    >
                      Detach
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={Boolean(toDetach)} onClose={closeDetach}>
        <Box sx={confirmationDialogSx}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box sx={confirmationIconSx}>
              <Icon icon="proicons:alert-triangle" width="36" height="36" color="#C40000" />
            </Box>
            <Typography sx={confirmationTitleSx}>Detach integrator?</Typography>
            <Typography sx={confirmationTextSx}>
              {toDetach?.name} will no longer take part in this Initiative
              {toDetach?.signupChoice ? ', and the Initiative is removed from its sign-up choice' : ''}. DAREs
              that already joined keep their membership.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Reason (optional)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ pt: 1 }}>
              <Button variant="contained" onClick={detach} sx={confirmationPrimaryButtonSx}>Yes</Button>
              <Button onClick={closeDetach} sx={confirmationSecondaryButtonSx}>No</Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <NotificationSnackbar
        open={Boolean(success)}
        onClose={() => setSuccess('')}
        message={success}
        severity="success"
        contentSx={{ px: 2.5 }}
      />
    </Box>
  );
}
